import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Leaf } from "lucide-react";
import { toast } from "sonner";
import { isPremium } from "@/components/PremiumGate";

/**
 * Membership — Premium is not a feature list. Premium is continued cultivation.
 *
 * Every capability below is a genuinely-shipped feature. If a capability is described
 * here it must be delivered in the app. Copy is governed by
 * /app/CULTIV_LANGUAGE_SYSTEM.md §11 (Premium philosophy) and the shipping Language
 * Checklist §13.
 */

const FREE_CAPABILITIES = [
    {
        title: "Complete Stewardship Assessment",
        body: "One thorough assessment across all eight areas of your life — Heart, Body, Mind, Home, Relationships, Work & Calling, Rest, and Kingdom.",
    },
    {
        title: "Personalized Stewardship Overview",
        body: "A first summary of your garden — where you stand today, which areas are steady, and which are quietly asking for your attention this season.",
    },
    {
        title: "Practical biblical insights",
        body: "A short summary that names what Scripture teaches about your current stewardship — not decoration, but the principle itself and how it applies.",
    },
    {
        title: "Initial stewardship recommendations",
        body: "Two or three specific, achievable steps you can begin taking this week. Not aspirational. Not generic.",
    },
    {
        title: "Journal, prayer list, and rhythms",
        body: "A quiet place to name what you are noticing, carry what you are praying for, and cultivate small weekly practices.",
    },
    {
        title: "Daily Scripture and Daily Encouragement",
        body: "A verse each morning with a short teaching, and a three-sentence encouragement that names one biblical principle relevant to your current stewardship.",
    },
    {
        title: "Progress dashboard",
        body: "One view of your whole garden, refreshed as your stewardship grows.",
    },
    {
        title: "Ten Tend reflections each month",
        body: "Enough to sit with the questions this season is asking. If you outgrow ten, Premium continues the conversation without a ceiling.",
    },
    {
        title: "Retake the assessment every ninety days",
        body: "So your garden can be re-considered as it changes.",
    },
];

const PREMIUM_DEEPENINGS = [
    {
        title: "The complete Stewardship Blueprint",
        body: "A fully developed Blueprint of the garden God has entrusted to you — observations, pattern recognition, biblical interpretation, stewardship priorities, personalized prayers, reflection questions, and a thirty-day plan you can actually walk. Downloadable as a PDF you can keep.",
    },
    {
        title: "Unlimited Tend conversations",
        body: "Continue asking questions, reflecting, praying, processing decisions, and receiving biblical stewardship guidance without limits — for as long as this season requires.",
    },
    {
        title: "Dynamic Stewardship Insights",
        body: "As your journals, prayers, and habits grow, Cultiv continues recognizing meaningful patterns across your writing — naming the through-lines you may not see and helping your discernment grow with them.",
    },
    {
        title: "Scripture Companion",
        body: "For any verse in your Cultiv library, a full companion: biblical context, the principle taught, why it applies to your current stewardship, three reflection questions, and one practical application for the week.",
    },
    {
        title: "Personalized Prayer Companion",
        body: "Prayers generated from your current stewardship — priority areas, recent journal entries, active prayers on your heart — each rooted in a specific passage of Scripture rather than generic encouragement.",
    },
    {
        title: "Weekly Stewardship Review",
        body: "A quiet letter each Sunday morning that considers your last seven days honestly — patterns observed, biblical insights, stewardship priorities, suggested prayers, Scripture, reflection questions, and practical next steps.",
    },
    {
        title: "Blueprint Library",
        body: "Every Blueprint you have received, kept in one place — so you can look back and see how God has worked across seasons.",
    },
    {
        title: "Every future Cultiv module",
        body: "Marriage, Family, Parenting, Home Stewardship, Financial Stewardship, Calling, Leadership, Hospitality, Physical Stewardship, and Palm Movement — each inherited into your membership at no additional cost as they are released.",
    },
];

const priceLabel = (p) => {
    if (!p) return "";
    const dollars = (p.amount / 100).toFixed(p.amount % 100 === 0 ? 0 : 2);
    return `$${dollars}`;
};

export default function Membership() {
    const { user, refreshUser } = useAuth();
    const [prices, setPrices] = useState([]);
    const [busy, setBusy] = useState(false);
    const premium = isPremium(user);

    useEffect(() => {
        api.get("/billing/prices").then((r) => setPrices(r.data.prices || [])).catch(() => {});
        // Self-heal: if the user paid but the webhook didn't land, pull the truth from Stripe.
        // Runs silently on every free-tier Membership visit. Premium users skip.
        if (!premium && user?.user_id) {
            api.post("/billing/sync").then(async ({ data }) => {
                if (data?.synced && data.tier === "premium") {
                    await refreshUser();
                    toast.success("Your Premium is active — welcome.");
                }
            }).catch(() => {});
        }
        // eslint-disable-next-line
    }, [user?.user_id, premium]);

    const [syncing, setSyncing] = useState(false);
    const forceSync = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/billing/sync");
            await refreshUser();
            if (data?.tier === "premium") {
                toast.success("Your Premium is active — welcome.");
            } else if (data?.reason === "no_stripe_customer" || data?.reason === "no_subscriptions_on_customer") {
                toast.error("We couldn't find a paid subscription on your account.");
            } else {
                toast.error("Still no active subscription. If you just paid, wait a moment and try again.");
            }
        } catch {
            toast.error("Couldn't reach Stripe. Try again in a moment.");
        } finally {
            setSyncing(false);
        }
    };

    const upgrade = async (lookup_key) => {
        setBusy(true);
        try {
            const { data } = await api.post("/payments/checkout", {
                lookup_key,
                quantity: 1,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't start checkout.");
            setBusy(false);
        }
    };

    const openPortal = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/billing/portal", { return_url: window.location.origin + "/membership" });
            window.location.href = data.portal_url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't open billing portal.");
            setBusy(false);
        }
    };

    const [optin, setOptin] = useState(user?.weekly_review_optin ?? true);
    useEffect(() => { setOptin(user?.weekly_review_optin ?? true); }, [user?.weekly_review_optin]);
    const toggleOptin = async () => {
        const next = !optin;
        setOptin(next);
        try {
            await api.post("/reviews/weekly/optin", { optin: next });
            await refreshUser();
            toast.success(next ? "Sunday letters turned on." : "Sunday letters paused.");
        } catch { setOptin(!next); toast.error("Couldn't update preference."); }
    };

    const monthly = prices.find((p) => p.lookup_key === "eden_premium_monthly");
    const annual = prices.find((p) => p.lookup_key === "eden_premium_annual");
    const annualMonthlyEquiv = annual ? (annual.amount / 100 / 12).toFixed(2) : null;

    return (
        <div className="space-y-20">
            <header className="max-w-3xl">
                <div className="eyebrow-gold mb-4">Membership</div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[1.02]">
                    Continue cultivating.
                </h1>
                <p className="mt-8 prose-cultiv">
                    Free Cultiv is complete on its own. Someone who never moves past it will still have their garden
                    honestly assessed, still receive biblical insights, still journal, still pray, still grow. Premium
                    is not more features — it is continued cultivation for the person who wants ongoing biblical
                    guidance as they faithfully steward every area of the life God has entrusted to them.
                </p>
                {premium && (
                    <div className="mt-10 hairline rounded-md p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="premium-status-card">
                        <div>
                            <div className="eyebrow-gold mb-1">Continued cultivation · active</div>
                            <div className="text-sm">Renews {user?.current_period_end ? new Date(user.current_period_end).toLocaleDateString(undefined, { dateStyle: "long" }) : "shortly"}.</div>
                        </div>
                        <Button onClick={openPortal} disabled={busy} data-testid="manage-billing-btn" variant="outline" className="rounded-md border-foreground/15">
                            Manage billing
                        </Button>
                    </div>
                )}
            </header>

            {/* Free · Discover Your Garden */}
            <section className="grid md:grid-cols-2 gap-6">
                <div className="hairline rounded-lg p-8 md:p-10" data-testid="plan-free-card">
                    <div className="eyebrow mb-3">Free · Discover your garden</div>
                    <div className="font-serif text-4xl md:text-5xl leading-none">$0</div>
                    <div className="text-xs text-muted-foreground mt-2">Complete on its own — not a trial</div>
                    <div className="mt-6 h-px w-12 bg-foreground/15" />
                    <p className="mt-8 text-sm text-muted-foreground leading-relaxed">
                        Everything you need to clearly understand where you stand today and begin taking faithful next
                        steps in every area of your stewardship.
                    </p>
                    <ul className="mt-8 space-y-6">
                        {FREE_CAPABILITIES.map((c) => (
                            <li key={c.title} className="text-sm leading-relaxed">
                                <div className="font-serif text-lg leading-tight mb-1">{c.title}</div>
                                <div className="text-muted-foreground">{c.body}</div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Premium · Tend Deeper */}
                <div className="hairline rounded-lg p-8 md:p-10 relative overflow-hidden bg-[hsl(var(--eden-forest-deep))] text-[hsl(var(--eden-cream))]" data-testid="plan-premium-card">
                    <div className="eyebrow-gold mb-3">Premium · Tend deeper</div>
                    <div className="flex items-baseline gap-3 flex-wrap">
                        <div className="font-serif text-4xl md:text-5xl leading-none">{priceLabel(monthly)}</div>
                        <div className="text-xs text-[hsl(var(--eden-cream))]/60">/ month</div>
                        {annual && (
                            <div className="text-xs text-[hsl(var(--eden-cream))]/70">
                                — or {priceLabel(annual)}/year (≈${annualMonthlyEquiv}/mo)
                            </div>
                        )}
                    </div>
                    <div className="mt-6 h-px w-12 bg-[hsl(var(--eden-gold))]" />
                    <p className="mt-8 text-sm text-[hsl(var(--eden-cream))]/80 leading-relaxed">
                        For the person who wants continued biblical guidance — ongoing insights, personalized
                        prayer and Scripture companions, unlimited Tend, the complete Blueprint, and every future
                        module inherited into your membership.
                    </p>
                    <ul className="mt-8 space-y-6">
                        {PREMIUM_DEEPENINGS.map((c) => (
                            <li key={c.title} className="text-sm leading-relaxed">
                                <div className="font-serif text-lg leading-tight mb-1 text-[hsl(var(--eden-cream))]">{c.title}</div>
                                <div className="text-[hsl(var(--eden-cream))]/75">{c.body}</div>
                            </li>
                        ))}
                    </ul>

                    {!premium && (
                        <div className="mt-10 flex flex-wrap gap-2">
                            <Button
                                onClick={() => upgrade("eden_premium_monthly")}
                                disabled={busy || !monthly}
                                data-testid="upgrade-monthly-btn"
                                className="bg-[hsl(var(--eden-cream))] text-[hsl(var(--eden-forest))] hover:bg-white rounded-md h-11 px-6 text-xs uppercase tracking-[0.3em]"
                            >
                                Continue · Monthly
                            </Button>
                            {annual && (
                                <Button
                                    onClick={() => upgrade("eden_premium_annual")}
                                    disabled={busy}
                                    data-testid="upgrade-annual-btn"
                                    variant="outline"
                                    className="border-[hsl(var(--eden-cream))]/40 text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-cream))]/10 hover:text-[hsl(var(--eden-cream))] rounded-md h-11 px-6 text-xs uppercase tracking-[0.3em] bg-transparent"
                                >
                                    Yearly · give up two months
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </section>

            <section className="text-center text-xs text-muted-foreground max-w-2xl mx-auto leading-relaxed space-y-4">
                <p>
                    Cancel any time. Managed through Stripe. No hidden costs. The garden belongs to you; Cultiv is only
                    a careful companion in the tending of it.
                </p>
                {!premium && (
                    <p data-testid="already-paid-nudge">
                        Already paid but this still shows Free?{" "}
                        <button
                            onClick={forceSync}
                            disabled={syncing}
                            data-testid="force-sync-btn"
                            className="underline hover:text-foreground transition-colors disabled:opacity-50"
                        >
                            {syncing ? "Checking with Stripe…" : "Refresh from Stripe"}
                        </button>
                    </p>
                )}
            </section>

            {premium && (
                <section
                    data-testid="weekly-optin-card"
                    className="hairline rounded-lg p-6 md:p-8 max-w-3xl"
                >
                    <div className="flex items-start justify-between gap-6 flex-wrap">
                        <div className="max-w-xl">
                            <div className="eyebrow-gold mb-2 flex items-center gap-2">
                                <Leaf className="w-3 h-3" strokeWidth={1.5} /> Sunday letter
                            </div>
                            <h2 className="font-serif text-2xl">A quiet consideration of your week</h2>
                            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                                Every Sunday morning a short letter considers your last seven days honestly — what
                                moved, what quieted, where the pattern is asking to be seen — and arrives in your
                                inbox before the week begins again.
                            </p>
                        </div>
                        <button
                            onClick={toggleOptin}
                            data-testid="weekly-optin-toggle"
                            className={`relative w-14 h-8 rounded-full transition-colors ${
                                optin ? "bg-[hsl(var(--eden-forest))]" : "bg-foreground/15"
                            }`}
                            aria-pressed={optin}
                            aria-label="Toggle Sunday letters"
                        >
                            <span className={`absolute top-1 w-6 h-6 rounded-full bg-[hsl(var(--eden-cream))] transition-transform ${optin ? "translate-x-7" : "translate-x-1"}`} />
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
}
